import React from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, FileText, Hash, Loader2, Megaphone, MessageSquare, Building2, Users,
  Search as SearchIcon,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AuthorLink } from "@/components/social/AuthorLink";
import { profileHref } from "@/components/social/AuthorLink";
import { timeAgo } from "@/lib/timeago";

const ICONS = {
  members: Users, posts: MessageSquare, comments: MessageSquare,
  announcements: Megaphone, messages: MessageSquare, streams: Hash,
  events: CalendarDays, resources: FileText, organizations: Building2,
};

function Row({ children, onClick, to, testId }) {
  const cls = "block w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-secondary";
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testId}>
      {children}
    </button>
  );
}

function Snippet({ text }) {
  return <p className="line-clamp-2 text-sm text-foreground/80">{text}</p>;
}

function Meta({ children }) {
  return <p className="mt-0.5 text-xs text-muted-foreground">{children}</p>;
}

/**
 * Group search results, rendered in the SAME centre column the Stream feed uses, grouped
 * by type with a few of each and "See all N" per group (owner's choice). Every result is a
 * way back to where the thing actually lives — a post opens its Stream focused on that
 * post, a message opens that conversation, a person opens their profile — so search is
 * navigation, not a dead end.
 */
export function GroupSearchResults({ group, query, onOpenPost, onOpenStream,
                                      onOpenThread, onClear }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [section, setSection] = React.useState(null);

  React.useEffect(() => { setSection(null); }, [query]);
  React.useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/groups/${group.id}/search`,
      { params: { q: query, section: section || undefined, limit: section ? 20 : 5 } })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(() => { if (live) setData({ sections: [], total: 0 }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [group.id, query, section]);

  if (loading) {
    return (
      <Card className="flex items-center justify-center border-0 p-10"
            data-testid="group-search-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  const sections = data?.sections || [];
  return (
    <div className="space-y-3" data-testid="group-search-results">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold" data-testid="group-search-heading">
          {data?.total ? `${data.total}${data.total >= 200 ? "+" : ""} result${data.total === 1 ? "" : "s"}` : "No results"}
          {" for "}&ldquo;{query}&rdquo;
        </h2>
        {section ? (
          <Button size="sm" variant="ghost" onClick={() => setSection(null)}
                  data-testid="group-search-back">All results</Button>
        ) : null}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onClear}
                data-testid="group-search-clear">Back to streams</Button>
      </div>

      {sections.length === 0 ? (
        <Card className="border-0 p-8 text-center" data-testid="group-search-empty">
          <SearchIcon className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Nothing in this group matches that yet. Try a shorter word, or a person&apos;s name.
          </p>
        </Card>
      ) : null}

      {sections.map((s) => {
        const Icon = ICONS[s.section] || SearchIcon;
        return (
          <Card key={s.section} className="border-0 p-4"
                data-testid={`group-search-section-${s.section}`}>
            <div className="mb-2 flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">
                {s.label}
              </h3>
              <Badge variant="secondary" className="text-[11px]">
                {s.total >= 200 ? "200+" : s.total}
              </Badge>
              {!section && s.has_more ? (
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs"
                        onClick={() => setSection(s.section)}
                        data-testid={`group-search-see-all-${s.section}`}>
                  See all {s.total >= 200 ? "200+" : s.total}
                </Button>
              ) : null}
            </div>
            <div className="space-y-1">
              {s.items.map((it) => {
                if (s.section === "members") {
                  return (
                    <Row key={it.user_id} to={profileHref(it) || "#"}
                         testId={`group-search-member-${it.user_id}`}>
                      <span className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          {it.headshot_url
                            ? <AvatarImage src={fileUrl(it.headshot_url)} alt="" /> : null}
                          <AvatarFallback className="bg-teal-50 text-[11px] text-teal-700">
                            {(it.display_name || "?")[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-[560]">
                            {it.display_name}
                          </span>
                          {it.headline ? (
                            <span className="block truncate text-xs text-muted-foreground">
                              {it.headline}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </Row>
                  );
                }
                if (s.section === "organizations") {
                  return (
                    <Row key={it.profile_id || it.id} to={profileHref(it) || "#"}
                         testId={`group-search-org-${it.profile_id || it.id}`}>
                      <span className="text-sm font-[560]">{it.display_name}</span>
                      <Meta>{it.profile_type || it.target_type}</Meta>
                    </Row>
                  );
                }
                if (s.section === "streams") {
                  return (
                    <Row key={it.id} onClick={() => onOpenStream(it.id)}
                         testId={`group-search-stream-${it.id}`}>
                      <span className="text-sm font-[560]">
                        #{it.name}{it.archived ? " · archived" : ""}
                      </span>
                      {it.snippet ? <Snippet text={it.snippet} /> : null}
                    </Row>
                  );
                }
                if (s.section === "messages") {
                  return (
                    <Row key={it.id} onClick={() => onOpenThread(it.thread_id)}
                         testId={`group-search-message-${it.id}`}>
                      <Snippet text={it.snippet} />
                      <Meta>
                        {it.sender?.display_name || "Member"} · {it.thread_label}
                        {" · "}{timeAgo(it.created_at)}
                      </Meta>
                    </Row>
                  );
                }
                if (s.section === "events" || s.section === "resources") {
                  const base = s.section === "events" ? "/e/" : "/r/";
                  return (
                    <Row key={it.id} to={it.slug ? `${base}${it.slug}` : "#"}
                         testId={`group-search-${s.section}-${it.id}`}>
                      <span className="text-sm font-[560]">{it.title}</span>
                      {it.snippet ? <Snippet text={it.snippet} /> : null}
                    </Row>
                  );
                }
                // Posts, announcements and comments all deep-link to the canonical post
                // in its own Stream.
                return (
                  <Row key={it.id}
                       onClick={() => onOpenPost(it.stream_id, it.post_id || it.id)}
                       testId={`group-search-${s.section}-${it.id}`}>
                    <Snippet text={it.snippet} />
                    <Meta>
                      <AuthorLink card={it.author} className="hover:text-primary"
                                  fallback="Member" />
                      {it.stream_name ? ` · #${it.stream_name}` : ""}
                      {s.section === "comments" ? " · comment" : ""}
                      {it.notified_everyone ? " · @everyone" : ""}
                      {" · "}{timeAgo(it.created_at)}
                    </Meta>
                  </Row>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
